import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, Grid, Paper, IconButton, CircularProgress } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { Minimize } from '@mui/icons-material';
import clientService from 'src/clientService';
import { useAppState } from 'src/state-management/state-provider';
import { AcousticIndexEnum, ChartRequest, ChartResponse } from 'src/api/Api';
import IndexChart from './IndexChart';
import DeploymentChart from './DeploymentChart';
import ProjectLevelChart from './ProjectLevelChart';
import ActivityTrendChart from './ActivityTrendChart';
import DeploymentActivityTrendChart from './DeploymentActivityTrendChart';

interface AnalyticsPanelProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
}

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({
  isOpen,
  isMaximized,
  onClose,
  onMaximize,
}) => {
  const { state } = useAppState();
  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [selectedDeployments, setSelectedDeployments] = useState<string[]>([]);
  const [loadingIndex, setLoadingIndex] = useState(false);
  const [loadingDeployment, setLoadingDeployment] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);

  const fetchChartData = async (deployments: string[]) => {
    if (deployments.length === 0) {
      setChartData(null);
      return;
    }

    setLoadingIndex(true);
    setLoadingDeployment(true);
    setLoadingProject(true);

    try {
      const req: ChartRequest = {
        deploymentIds: deployments,
        index: [
          AcousticIndexEnum.Aci,
          AcousticIndexEnum.Bi,
          AcousticIndexEnum.Adi,
          AcousticIndexEnum.Aei,
          AcousticIndexEnum.Ndsi,
        ],
      };

      const res =
        await clientService.api.createDeploymentChartsApiV1DeploymentsBioInsituChartPost(req);
      setChartData(res.data);
    } catch (err) {
      console.error(err);
      setChartData(null);
    } finally {
      setLoadingIndex(false);
      setLoadingDeployment(false);
      setLoadingProject(false);
    }
  };

  useEffect(() => {
    if (isOpen && state.bioInteractiveMapFilter.selectedDeployment) {
      const depId = state.bioInteractiveMapFilter.selectedDeployment.deployment_id;
      setSelectedDeployments([depId]);
      fetchChartData([depId]);
    } else {
      setSelectedDeployments([]);
    }
  }, [isOpen, state.bioInteractiveMapFilter]);

  return (
    <Drawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      variant="temporary"
      ModalProps={{ hideBackdrop: true }}
      PaperProps={{
        sx: {
          zIndex: 999,
          height: isMaximized ? '100%' : '50%',
          maxHeight: '90vh',
          borderTop: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          transition: (theme) =>
            theme.transitions.create('height', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Project Analytics</Typography>
          <Box>
            <IconButton onClick={onMaximize}>
              {isMaximized ? <Minimize /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              mb: 2,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              display: 'inline-block',
              pb: 0.5,
            }}
          >
            Acoustic Indices Analysis
          </Typography>
        </Grid>
        {!state.bioInteractiveMapFilter.selectedDeployment ? (
          // Show Project Level Chart
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, mb: 2, minHeight: '450px', height: 'auto' }}>
              {loadingProject ? (
                <Box
                  sx={{
                    mt: '25%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography sx={{ ml: 2 }}>Loading project-level chart...</Typography>
                </Box>
              ) : state.selectedProject?.bioMapData?.deployments.length ? (
                <ProjectLevelChart
                  projectDeployments={state.selectedProject?.bioMapData?.deployments || []}
                />
              ) : (
                <Typography>No project-level chart data available</Typography>
              )}
            </Paper>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2, height: '500px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography color="textSecondary">
                      Select a deployment in filter panel to see Index level chart
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, mb: 2, height: '500px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography color="textSecondary">
                      Select a deployment in filter panel to Deployment level chart
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          // Show Index and Deployment Charts
          <Grid container spacing={2}>
            {/* Index Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '500px' }}>
                {loadingIndex ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <CircularProgress size={32} />
                    <Typography sx={{ ml: 1 }}>Loading index chart...</Typography>
                  </Box>
                ) : chartData && chartData.comparison?.length ? (
                  <IndexChart
                    chartData={chartData}
                    deploymentName={
                      state.bioInteractiveMapFilter.selectedDeployment?.deployment_name ||
                      'Deployment'
                    }
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography color="textSecondary">
                      Select a deployment in filter panel to see this chart
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Deployment Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '500px' }}>
                {loadingDeployment ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <CircularProgress size={32} />
                    <Typography sx={{ ml: 1 }}>Loading deployment chart...</Typography>
                  </Box>
                ) : selectedDeployments.length && chartData?.comparison?.length ? (
                  <DeploymentChart
                    initialDeployments={selectedDeployments}
                    projectDeployments={state.selectedProject?.bioMapData?.deployments || []}
                    selectedLocation={state.bioInteractiveMapFilter.selectedLocation}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography color="textSecondary">
                      Select a deployment in filter panel to see this chart
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  borderBottom: '2px solid',
                  borderColor: 'primary.main',
                  display: 'inline-block',
                  pb: 0.5,
                }}
              >
                Activity Trend Analysis
              </Typography>
            </Grid>

            <Grid container spacing={2} padding={2}>
              {/* Single Deployment Activity Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '500px' }}>
                  {state.bioInteractiveMapFilter.selectedDeployment &&
                  chartData &&
                  chartData.comparison?.length ? (
                    <ActivityTrendChart
                      chartData={chartData}
                      deploymentName={
                        state.bioInteractiveMapFilter.selectedDeployment?.deployment_name ||
                        'Deployment'
                      }
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h6" color="textSecondary">
                        Single Deployment Activity Trends
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Select a deployment to view its activity trend data
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Multi-Deployment Species Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: '500px' }}>
                  <DeploymentActivityTrendChart
                    projectDeployments={state.selectedProject?.bioMapData?.deployments || []}
                    selectedLocation={state.bioInteractiveMapFilter.selectedLocation}
                    selectedDeployment={state.bioInteractiveMapFilter.selectedDeployment}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
      </Box>
    </Drawer>
  );
};

export default AnalyticsPanel;
